import { useEffect, useState } from "react";
import {
  Box,
  Button,
  InputAdornment,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  getOrderHistoryRequest,
  getOrderHistorySuccess,
} from "../../../../redux/orders/slice";
import Loading from "../../../../components/loading/loading";
import AddIcon from "@mui/icons-material/Add";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import HistoryList from "./history-list";
import AddOrderUpdate from "./add-order-update";
import dayjs from "dayjs";
import Notification from "../../../../components/notification/notification";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}
function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

function TabLabel(data: any) {
  return <>{data?.label}</>;
}

export default function History({ orderId, navigateToIssues }: any) {
  const dispatch = useDispatch();
  const [value, setValue] = useState(0);
  const [allHistory, setAllHistory] = useState<any[]>([]);
  const [orderUpdates, setOrderUpdates] = useState<any[]>([]);
  const [assignment, setAssignment] = useState<any[]>([]);
  const [orderStatus, setOrderStatus] = useState<any[]>([]);
  const [searchedAllHistory, setSearchedAllHistory] = useState<any[]>([]);
  const [searchedOrderUpdates, setSearchedOrderUpdates] = useState<any[]>([]);
  const [searchedAssignment, setSearchedAssignment] = useState<any[]>([]);
  const [searchedOrderStatus, setSearchedOrderStatus] = useState<any[]>([]);
  const [showAddUpdate, setShowAddUpdate] = useState(false);
  const [searchTxt, setSearchTxt] = useState("");
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const { loading, orderHistory } = useSelector((state: any) => state.orders);

  useEffect(() => {
    dispatch(getOrderHistorySuccess({ response: null }));
    dispatch(getOrderHistoryRequest({ orderId }));
  }, []); // eslint-disable-line

  useEffect(() => {
    if (orderHistory) {
      const orderUpdatesHistory = JSON.parse(
        JSON.stringify(orderHistory?.orderUpdates)
      );
      const orderAssignmentHistory = JSON.parse(
        JSON.stringify(orderHistory?.orderAssignmentHistories)
      );
      const orderStatusHistory = JSON.parse(
        JSON.stringify(orderHistory?.orderHistories)
      );
      const sortedOrderUpdatesHistory = orderUpdatesHistory.sort(
        (a: any, b: any) =>
          dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf()
      );
      const sortedOrderAssignmentHistory = orderAssignmentHistory.sort(
        (a: any, b: any) =>
          dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf()
      );
      const sortedOrderStatusHistory = orderStatusHistory.sort(
        (a: any, b: any) =>
          dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf()
      );
      const sortedAllHistory = [
        ...sortedOrderUpdatesHistory,
        ...sortedOrderAssignmentHistory,
        ...sortedOrderStatusHistory,
      ].sort(
        (a: any, b: any) =>
          dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf()
      );

      sortedOrderUpdatesHistory.forEach((history: any) => {
        history.description = `Order Update (${history?.actionType}): ${history?.description}`;
        history.createdAt = dayjs(history?.createdAt).format(
          "MM/DD/YYYY h:mm A"
        );
      });
      sortedOrderAssignmentHistory.forEach((history: any) => {
        history.createdAt = dayjs(history?.createdAt).format(
          "MM/DD/YYYY h:mm A"
        );
        history.userFullName = history?.isUpdatedBySystem
          ? "System"
          : history.userFullName;
        if (history?.actionType === "Accepted") {
          history.description = "Inspector Accepted";
        } else if (history?.actionType === "Rejected") {
          history.description = `Declined: ${history.description}`;
        }
        if (history?.inspectorFirmName) {
          history.userFullName = `${history.userFullName} - ${history?.inspectorFirmName}`;
        }
      });
      sortedOrderStatusHistory.forEach((history: any) => {
        history.createdAt = dayjs(history?.createdAt).format(
          "MM/DD/YYYY h:mm A"
        );
        history.userFullName = history?.isUpdatedBySystem
          ? "System"
          : history.userFullName;
      });
      setAllHistory(sortedAllHistory);
      setOrderUpdates(sortedOrderUpdatesHistory);
      setAssignment(sortedOrderAssignmentHistory);
      setOrderStatus(sortedOrderStatusHistory);
    }
  }, [orderHistory]);

  useEffect(() => {
    if (searchTxt) {
      searchHistory();
    }
  }, [searchTxt]); // eslint-disable-line

  const searchHistory = () => {
    const searchedAllHistory = allHistory?.filter(
      (history) =>
        history?.description
          ?.toLowerCase()
          .includes(searchTxt?.toLowerCase()) ||
        history?.userFullName
          ?.toLowerCase()
          .includes(searchTxt?.toLowerCase()) ||
        history?.createdAt?.toLowerCase().includes(searchTxt?.toLowerCase())
    );
    const searchedUpdateHistory = orderUpdates?.filter(
      (history) =>
        history?.description
          ?.toLowerCase()
          .includes(searchTxt?.toLowerCase()) ||
        history?.userFullName
          ?.toLowerCase()
          .includes(searchTxt?.toLowerCase()) ||
        history?.createdAt?.toLowerCase().includes(searchTxt?.toLowerCase())
    );
    const searchedAssignmentHistory = assignment?.filter(
      (history) =>
        history?.description
          ?.toLowerCase()
          .includes(searchTxt?.toLowerCase()) ||
        history?.userFullName
          ?.toLowerCase()
          .includes(searchTxt?.toLowerCase()) ||
        history?.createdAt?.toLowerCase().includes(searchTxt?.toLowerCase())
    );
    const searchedStatusHistory = orderStatus?.filter(
      (history) =>
        history?.description
          ?.toLowerCase()
          .includes(searchTxt?.toLowerCase()) ||
        history?.userFullName
          ?.toLowerCase()
          .includes(searchTxt?.toLowerCase()) ||
        history?.createdAt?.toLowerCase().includes(searchTxt?.toLowerCase())
    );
    setSearchedAllHistory(searchedAllHistory);
    setSearchedOrderUpdates(searchedUpdateHistory);
    setSearchedAssignment(searchedAssignmentHistory);
    setSearchedOrderStatus(searchedStatusHistory);
  };

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const orderUpdateSuccess = () => {
    setShowSuccessNotification(true);
    setSuccessMessage("Order updated Successfully.");
  };

  return (
    <Box mx={3}>
      <Box
        sx={{
          mt: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h5">History</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setShowAddUpdate(true)}
        >
          Add Order Update
        </Button>
      </Box>
      <Box
        sx={{
          mt: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Tabs
          value={value}
          onChange={handleChange}
          aria-label="order history tabs"
          variant="scrollable"
          scrollButtons={false}
        >
          <Tab className="tab-label" label={<TabLabel label="All HIstory" />} />
          <Tab
            className="tab-label"
            label={<TabLabel label="Order Updates" />}
          />
          <Tab className="tab-label" label={<TabLabel label="Assignment" />} />
          <Tab
            className="tab-label"
            label={<TabLabel label="Order Status" />}
          />
        </Tabs>
        <TextField
          sx={{
            backgroundColor: "white",
            height: 40,
            width: 300,
            borderRadius: 1,
            ".MuiOutlinedInput-root": {
              height: 40,
              paddingTop: 0,
              paddingBottom: 0,
            },
          }}
          id="search"
          variant="outlined"
          placeholder="Search"
          value={searchTxt}
          onChange={(e) => setSearchTxt(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlinedIcon />
                </InputAdornment>
              ),
            },
          }}
        />
      </Box>
      <CustomTabPanel value={value} index={0}>
        <HistoryList
          history={searchTxt.length > 0 ? searchedAllHistory : allHistory}
        />
      </CustomTabPanel>
      <CustomTabPanel value={value} index={1}>
        <HistoryList
          history={searchTxt.length > 0 ? searchedOrderUpdates : orderUpdates}
        />
      </CustomTabPanel>
      <CustomTabPanel value={value} index={2}>
        <HistoryList
          history={searchTxt.length > 0 ? searchedAssignment : assignment}
        />
      </CustomTabPanel>
      <CustomTabPanel value={value} index={3}>
        <HistoryList
          history={searchTxt.length > 0 ? searchedOrderStatus : orderStatus}
        />
      </CustomTabPanel>
      {showAddUpdate && (
        <AddOrderUpdate
          orderId={orderId}
          showDialog={showAddUpdate}
          closeDialog={() => setShowAddUpdate(false)}
          navigateToIssues={navigateToIssues}
          orderUpdateSuccess={orderUpdateSuccess}
        />
      )}
      {showSuccessNotification && (
        <Notification
          show={showSuccessNotification}
          message={successMessage}
          onClose={() => setShowSuccessNotification(false)}
        />
      )}
      {loading && <Loading />}
    </Box>
  );
}
